import { useEffect } from 'react';
import { getParentOrigin } from '@/utils/parentOrigin';

/**
 * Custom hook to notify the parent iframe when the component's size changes.
 * This allows the parent to resize the iframe to fit the content properly.
 * Measures #root so height shrinks when content collapses (e.g. accordion).
 * Uses ResizeObserver so collapse/expand is detected even without dependency changes.
 *
 * @param dependencies - Array of dependencies that trigger resize notifications when they change
 */
function getContentHeight(): number {
  const root = document.getElementById('root');
  if (root) {
    const rootHeight = Math.max(root.scrollHeight, root.offsetHeight);
    return rootHeight;
  }
  return Math.max(
    document.body.scrollHeight,
    document.body.offsetHeight,
    document.documentElement.scrollHeight,
    document.documentElement.offsetHeight
  );
}

function notifyParentHeight() {
  if (window.parent === window) return;
  requestAnimationFrame(() => {
    requestAnimationFrame(() => {
      const height = getContentHeight();
      window.parent.postMessage({ type: 'ui-size-change', payload: { height } }, getParentOrigin());
    });
  });
}

export function useIframeResize(dependencies: React.DependencyList = []) {
  useEffect(() => {
    if (window.parent === window) return;

    // Delay must exceed Ant Design's motionDurationMid (200ms) so the
    // open/close slide animation finishes before we measure content height.
    const timeoutId = setTimeout(notifyParentHeight, 250);

    // ResizeObserver: fire when #root size actually changes so collapse shrinks iframe
    const root = document.getElementById('root');
    if (root && typeof ResizeObserver !== 'undefined') {
      const ro = new ResizeObserver(() => notifyParentHeight());
      ro.observe(root);
      return () => {
        clearTimeout(timeoutId);
        ro.disconnect();
      };
    }

    return () => clearTimeout(timeoutId);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, dependencies);
}
